import * as Sentry from '@sentry/nextjs';

type Entry = {
  value?: unknown;
  storedAt: number;
  pending?: Promise<unknown>;
};

type ServerCacheOptions = {
  freshMs: number;
  staleMs: number;
  maxEntries: number;
  now?: () => number;
  onBackgroundError?: (error: unknown, key: string) => void;
};

export function createServerCache({
  freshMs,
  staleMs,
  maxEntries,
  now = Date.now,
  onBackgroundError = () => undefined,
}: ServerCacheOptions) {
  const entries = new Map<string, Entry>();

  function evictOldest() {
    while (entries.size > maxEntries) {
      const oldestKey = entries.keys().next().value;
      if (oldestKey === undefined) return;
      entries.delete(oldestKey);
    }
  }

  function load<T>(key: string, loader: () => Promise<T>): Promise<T> {
    const previous = entries.get(key);
    const pending = loader().then(
      (value) => {
        const current = entries.get(key);
        if (!current || current.pending === pending) {
          entries.delete(key);
          entries.set(key, { value, storedAt: now() });
          evictOldest();
        }
        return value;
      },
      (error) => {
        if (entries.get(key)?.pending === pending) {
          if (previous && 'value' in previous) {
            entries.set(key, { value: previous.value, storedAt: previous.storedAt });
          } else {
            entries.delete(key);
          }
        }
        throw error;
      }
    );

    entries.set(key, { ...previous, storedAt: previous?.storedAt ?? 0, pending });
    evictOldest();

    return pending;
  }

  async function get<T>(key: string, loader: () => Promise<T>): Promise<T> {
    const entry = entries.get(key);
    const age = entry ? now() - entry.storedAt : Infinity;

    if (entry && 'value' in entry && age < staleMs) {
      if (age >= freshMs && !entry.pending) {
        load(key, loader).catch((error) => onBackgroundError(error, key));
      }
      return structuredClone(entry.value as T);
    }

    const value = await ((entry?.pending as Promise<T> | undefined) ?? load(key, loader));
    return structuredClone(value);
  }

  function clear() {
    entries.clear();
  }

  return { get, clear };
}

const statusOf = (error: unknown) =>
  (error as { response?: { status?: number } } | undefined)?.response?.status;

export function createBackgroundErrorReporter({
  intervalMs,
  maxKeys,
  now = Date.now,
}: {
  intervalMs: number;
  maxKeys: number;
  now?: () => number;
}) {
  const reportedAt = new Map<string, number>();

  return (error: unknown, key: string) => {
    if (statusOf(error) === 404) return;

    const current = now();
    const last = reportedAt.get(key);
    if (last !== undefined && current - last < intervalMs) return;

    reportedAt.delete(key);
    for (const [oldestKey, oldestAt] of reportedAt) {
      if (reportedAt.size < maxKeys && current - oldestAt < intervalMs) break;
      reportedAt.delete(oldestKey);
    }
    reportedAt.set(key, current);
    Sentry.captureException(error, { tags: { server_cache_key: key } });
  };
}

const FRESH_MS = 60_000;
const MAX_ENTRIES = 2000;

const serverCache = createServerCache({
  freshMs: FRESH_MS,
  staleMs: 300_000,
  maxEntries: MAX_ENTRIES,
  onBackgroundError: createBackgroundErrorReporter({ intervalMs: FRESH_MS, maxKeys: MAX_ENTRIES }),
});

export function withServerCache<T>(key: string, loader: () => Promise<T>): Promise<T> {
  if (typeof window !== 'undefined') {
    return loader();
  }
  return serverCache.get(key, loader);
}
